// P1-014: Hardware shop + installed hardware management panel
import { useState } from 'react';
import { HardwareType } from '@/shared/types/common';
import { useHardwareStore } from '../stores/hardwareStore';
import { HARDWARE_CONFIGS } from '../data/hardware-configs';
import type { HardwareConfig } from '../types/hardware-internal';
import { HardwareManager } from '../systems/HardwareManager';
import { Panel } from '@/shared/ui/Panel';
import { Button } from '@/shared/ui/Button';
import { HardwareCard } from './HardwareCard';
import { applyInflation } from '@/shared/formulas/economy';
import { usePlayerStore } from '@/shared/stores/playerStore';

/** Tab definitions for hardware categories */
const HW_TABS: { type: HardwareType | 'ALL'; label: string }[] = [
  { type: 'ALL',              label: '全部' },
  { type: HardwareType.CPU,   label: 'CPU' },
  { type: HardwareType.GPU,   label: 'GPU' },
  { type: HardwareType.TPU,   label: 'TPU' },
  { type: HardwareType.COOLER, label: '散热' },
  { type: HardwareType.PSU,   label: '电源' },
];

export function ServerRackPanel() {
  const [activeTab, setActiveTab] = useState<HardwareType | 'ALL'>('ALL');
  const [selectedShopItem, setSelectedShopItem] = useState<string | null>(null);
  const [selectedOwnedItem, setSelectedOwnedItem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const completedProjects = usePlayerStore((s) => s.completedProjects);
  const slots = useHardwareStore((s) => s.slots);
  const inventory = useHardwareStore((s) => s.inventory);
  const totalCompute = useHardwareStore((s) => s.getTotalCompute());
  const powerUsage = useHardwareStore((s) => s.getTotalPower());
  const powerLimit = useHardwareStore((s) => s.getTotalPowerLimit());
  const heatGen = useHardwareStore((s) => s.getTotalHeatGen());
  const cooling = useHardwareStore((s) => s.getTotalCooling());
  const overloadValue = useHardwareStore((s) => s.getOverloadValue());

  // Get catalog items filtered by tab
  const catalogItems: HardwareConfig[] = Object.values(HARDWARE_CONFIGS).filter(
    (c) => activeTab === 'ALL' || c.type === activeTab
  );

  // Find selected items for detail view
  const shopConfig = selectedShopItem ? HARDWARE_CONFIGS[selectedShopItem] : null;
  const ownedItem = selectedOwnedItem
    ? inventory.find((i) => i.instanceId === selectedOwnedItem) ??
      slots.find((s) => s.installed?.instanceId === selectedOwnedItem)?.installed ??
      null
    : null;

  const detailItem = ownedItem ?? shopConfig ?? null;
  const isFromInventory = ownedItem ? inventory.some((i) => i.instanceId === ownedItem.instanceId) : false;
  const isInstalled = ownedItem ? slots.some((s) => s.installed?.instanceId === ownedItem.instanceId) : false;

  // Find first empty slot
  const firstEmptySlot = slots.findIndex((s) => s.installed === null);

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 2000);
  };

  // Action handlers
  const handleBuy = () => {
    if (!selectedShopItem) return;
    const result = HardwareManager.buyHardware(selectedShopItem);
    if (result.success) {
      showMessage('购买成功!');
    } else {
      showMessage(result.error ?? '购买失败');
    }
  };

  const handleInstall = () => {
    if (!ownedItem || !isFromInventory || firstEmptySlot === -1) return;
    const result = HardwareManager.installToSlot(ownedItem.instanceId, firstEmptySlot);
    if (result.success) {
      showMessage(`已安装到槽位 ${firstEmptySlot}`);
      setSelectedOwnedItem(null);
    } else {
      showMessage(result.error ?? '安装失败');
    }
  };

  const handleUninstall = () => {
    if (!ownedItem || !isInstalled) return;
    const slotIdx = slots.findIndex((s) => s.installed?.instanceId === ownedItem.instanceId);
    if (slotIdx === -1) return;
    const result = HardwareManager.uninstallFromSlot(slotIdx);
    if (result.success) {
      showMessage('已卸载');
      setSelectedOwnedItem(null);
    } else {
      showMessage(result.error ?? '卸载失败');
    }
  };

  const handleSell = () => {
    if (!ownedItem || !isFromInventory) return;
    const result = HardwareManager.sellHardware(ownedItem.instanceId);
    if (result.success) {
      showMessage(`出售获得 ₣${result.resellPrice ?? 0}`);
      setSelectedOwnedItem(null);
    } else {
      showMessage(result.error ?? '出售失败');
    }
  };

  // Status bar color helpers
  const powerColor = powerUsage > powerLimit ? 'danger' : 'ok';
  const heatColor = heatGen > cooling ? 'danger' : 'ok';
  const overloadColor = overloadValue > 10 ? 'danger' : overloadValue > 0 ? 'warning' : 'ok';

  return (
    <Panel title="硬件管理 — SERVER RACK" glow className="server-rack">
      <div className="server-rack__top">
        {/* Left: Shop catalog */}
        <div className="server-rack__shop">
          {/* Category tabs */}
          <div className="server-rack__tabs">
            {HW_TABS.map((tab) => (
              <button
                key={tab.type}
                className={`server-rack__tab ${activeTab === tab.type ? 'server-rack__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.type)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Shop items list */}
          <div className="server-rack__catalog">
            {/* Installed items */}
            {slots.filter((s) => s.installed).map((s) => (
              <HardwareCard
                key={s.installed!.instanceId}
                item={s.installed!}
                status="installed"
                selected={selectedOwnedItem === s.installed!.instanceId}
                onClick={() => {
                  setSelectedOwnedItem(s.installed!.instanceId);
                  setSelectedShopItem(null);
                }}
              />
            ))}

            {/* Inventory items */}
            {inventory.map((item) => (
              <HardwareCard
                key={item.instanceId}
                item={item}
                status="inventory"
                selected={selectedOwnedItem === item.instanceId}
                onClick={() => {
                  setSelectedOwnedItem(item.instanceId);
                  setSelectedShopItem(null);
                }}
              />
            ))}

            {/* Separator */}
            {(slots.some((s) => s.installed) || inventory.length > 0) && (
              <div style={{
                borderTop: '1px solid var(--border-color)',
                margin: 'var(--space-sm) 0',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                paddingTop: 'var(--space-xs)',
              }}>
                -- 商店 --
              </div>
            )}

            {/* Catalog items */}
            {catalogItems.map((config) => (
              <HardwareCard
                key={config.id}
                item={config}
                status="shop"
                selected={selectedShopItem === config.id}
                onClick={() => {
                  setSelectedShopItem(config.id);
                  setSelectedOwnedItem(null);
                }}
              />
            ))}
          </div>
        </div>

        {/* Right: Detail + actions */}
        <div className="server-rack__detail">
          {!detailItem ? (
            <div className="server-rack__detail-placeholder">
              选择一个硬件查看详情
            </div>
          ) : (
            <Panel className="server-rack__detail-info">
              <div className="server-rack__detail-name">{detailItem.name}</div>

              <div className="server-rack__detail-stats">
                <div className="server-rack__detail-stat">
                  <span className="server-rack__detail-stat-label">类型</span>
                  <span className="server-rack__detail-stat-value">{detailItem.type}</span>
                </div>
                {detailItem.compute > 0 && (
                  <div className="server-rack__detail-stat">
                    <span className="server-rack__detail-stat-label">算力</span>
                    <span className="server-rack__detail-stat-value" style={{ color: 'var(--color-primary)' }}>+{detailItem.compute}</span>
                  </div>
                )}
                <div className="server-rack__detail-stat">
                  <span className="server-rack__detail-stat-label">
                    {('isPSU' in detailItem && detailItem.isPSU) ? '供电上限' : '功耗'}
                  </span>
                  <span className="server-rack__detail-stat-value">{detailItem.power}</span>
                </div>
                {detailItem.type === HardwareType.COOLER ? (
                  <div className="server-rack__detail-stat">
                    <span className="server-rack__detail-stat-label">散热容量</span>
                    <span className="server-rack__detail-stat-value" style={{ color: 'var(--color-info)' }}>+{detailItem.cooling}</span>
                  </div>
                ) : detailItem.cooling > 0 && detailItem.type !== HardwareType.PSU ? (
                  <div className="server-rack__detail-stat">
                    <span className="server-rack__detail-stat-label">发热量</span>
                    <span className="server-rack__detail-stat-value">{detailItem.cooling}</span>
                  </div>
                ) : null}
                <div className="server-rack__detail-stat">
                  <span className="server-rack__detail-stat-label">价格</span>
                  <span className="server-rack__detail-stat-value" style={{ color: 'var(--color-element-yellow)' }}>₣ {applyInflation(detailItem.price, completedProjects).toLocaleString()}</span>
                </div>
                {detailItem.specialEffect && (
                  <div className="server-rack__detail-stat">
                    <span className="server-rack__detail-stat-label">特殊效果</span>
                    <span className="server-rack__detail-stat-value" style={{ color: 'var(--color-accent)' }}>
                      {detailItem.specialEffect.type === 'REDUCE_CD'
                        ? `技能CD-${detailItem.specialEffect.value}`
                        : `过载阈值+${detailItem.specialEffect.value}%`}
                    </span>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="server-rack__actions">
                {/* Shop item → buy */}
                {shopConfig && !ownedItem && (
                  <Button variant="primary" onClick={handleBuy}>购买</Button>
                )}

                {/* Inventory item → install or sell */}
                {ownedItem && isFromInventory && (
                  <>
                    <Button
                      variant="primary"
                      onClick={handleInstall}
                      disabled={firstEmptySlot === -1}
                    >
                      安装
                    </Button>
                    <Button variant="danger" onClick={handleSell}>出售</Button>
                  </>
                )}

                {/* Installed item → uninstall */}
                {ownedItem && isInstalled && (
                  <Button variant="danger" onClick={handleUninstall}>卸载</Button>
                )}
              </div>

              {/* Message */}
              {message && (
                <div style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-accent)',
                  marginTop: 'var(--space-xs)',
                }}>
                  {message}
                </div>
              )}
            </Panel>
          )}
        </div>
      </div>

      {/* Bottom: Status bar */}
      <div className="server-rack__status-bar">
        <div className="server-rack__status-item">
          <span className="server-rack__status-label">算力:</span>
          <span className="server-rack__status-value" style={{ color: 'var(--color-primary)' }}>{totalCompute}</span>
        </div>
        <div className="server-rack__status-item">
          <span className="server-rack__status-label">功耗:</span>
          <span className={`server-rack__status-value server-rack__status-value--${powerColor}`}>
            {powerUsage}/{powerLimit}
          </span>
        </div>
        <div className="server-rack__status-item">
          <span className="server-rack__status-label">散热:</span>
          <span className={`server-rack__status-value server-rack__status-value--${heatColor}`}>
            {heatGen}/{cooling}
          </span>
        </div>
        <div className="server-rack__status-item">
          <span className="server-rack__status-label">过载:</span>
          <span className={`server-rack__status-value server-rack__status-value--${overloadColor}`}>
            {overloadValue > 0 ? `${overloadValue} [OVERLOAD]` : '0 [OK]'}
          </span>
        </div>
        <div className="server-rack__status-item">
          <span className="server-rack__status-label">槽位:</span>
          <span className="server-rack__status-value">
            {slots.filter((s) => s.installed).length}/{slots.length}
          </span>
        </div>
      </div>
    </Panel>
  );
}
